import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

// Column Filter - Only show if column has enableColumnFilter: true
const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  tableclassName?: any;
  divclassName?: any;
  thclassName?: any;
  trclassName?: any;
  tableClass?: any;
  tdclassName?: any;
  theadclassName?: any;
  tbodyclassName?: any;
  isTfoot?: boolean;
  isSelect?: boolean;
  isBordered?: boolean;
  customPageSize?: number;
  isGlobalFilter?: boolean;
  isPagination: boolean;
  PaginationClassName?: string;
  SearchPlaceholder?: string;

  // Server-side pagination props
  currentPage?: number;
  totalCount?: number;
  totalPages?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  // Server-side sorting props
  sortField?: string;
  sortOrder?: string;
  onSort?: (field: string, direction: string) => void;
}

const TableContainer = ({
  columns,
  data,
  tableclassName,
  theadclassName,
  divclassName,
  trclassName,
  thclassName,
  tdclassName,
  tbodyclassName,
  isTfoot,
  isSelect,
  isPagination,
  customPageSize,
  isGlobalFilter,
  PaginationClassName,
  SearchPlaceholder,

  // Server-side pagination props
  currentPage = 0,
  totalCount = 0,
  totalPages = 0,
  onPageChange,
  onPageSizeChange,

  // Server-side sorting props
  sortField = "",
  sortOrder = "",
  onSort,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [internalPageSize, setInternalPageSize] = useState(
    customPageSize || 10
  );
  const [currentSort, setCurrentSort] = useState<{
    field: string;
    direction: string;
  }>({
    field: sortField,
    direction: sortOrder,
  });

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  // Configure columns with proper defaults
  const configuredColumns = React.useMemo(() => {
    return columns.map((column: any) => ({
      ...column,
      // Default to false if not explicitly set to true
      enableSorting: column.enableSorting === true,
      enableColumnFilter: column.enableColumnFilter === true,
    }));
  }, [columns]);

  // For server-side pagination, we don't use react-table's internal pagination
  const table = useReactTable({
    columns: configuredColumns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Disable react-table's pagination for server-side
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // Important: We handle pagination manually
    manualSorting: true, // Important: We handle sorting manually
    pageCount: totalPages,
  });

  const { getHeaderGroups, getFooterGroups, getRowModel } = table;

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setInternalPageSize(newPageSize);
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  // Handle page change
  const handlePageChange = (newPageIndex: number) => {
    if (onPageChange) {
      onPageChange(newPageIndex);
    }
  };

  // Handle sorting - only if column has enableSorting: true
  const handleSort = (header: any) => {
    if (onSort && header.column.getCanSort()) {
      let newDirection = "asc";

      // If already sorting this field, toggle direction
      if (currentSort.field === header.column.id) {
        newDirection = currentSort.direction === "asc" ? "desc" : "asc";
      } else {
        // New field, default to ascending
        newDirection = "asc";
      }

      const newSortState = {
        field: header.column.id,
        direction: newDirection,
      };

      setCurrentSort(newSortState);
      onSort(newSortState.field, newSortState.direction);
    }
  };

  // Get sort icon for a column - only show if column has enableSorting: true
  const getSortIcon = (header: any) => {
    if (!header.column.getCanSort()) return null;

    if (currentSort.field !== header.column.id) {
      return (
        <div className="flex flex-col">
          <ChevronUp className="size-3 opacity-30" />
          <ChevronDown className="size-3 opacity-30 -mt-1" />
        </div>
      );
    }

    return currentSort.direction === "asc" ? (
      <ChevronUp className="size-3 text-blue-500" />
    ) : (
      <ChevronDown className="size-3 text-blue-500" />
    );
  };

  // Update current sort when props change
  useEffect(() => {
    if (sortField && sortOrder) {
      setCurrentSort({
        field: sortField,
        direction: sortOrder,
      });
    }
  }, [sortField, sortOrder]);

  // Calculate display values
  const getDisplayTotalCount = () => {
    return totalCount > 0 ? totalCount : data.length;
  };

  const getDisplayPageSize = () => {
    return internalPageSize;
  };

  const getDisplayCurrentPage = () => {
    return currentPage >= 0 ? currentPage : 0;
  };

  const getDisplayCanPreviousPage = () => {
    return getDisplayCurrentPage() > 0;
  };

  const getDisplayCanNextPage = () => {
    return getDisplayCurrentPage() < totalPages - 1;
  };

  // Generate page options for pagination
  const getDisplayPageOptions = () => {
    if (totalPages > 0) {
      // Show limited page numbers for better UX
      const current = getDisplayCurrentPage();
      const pages = [];
      const maxVisiblePages = 5;

      let startPage = Math.max(0, current - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

      // Adjust start page if we're near the end
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(0, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    }
    return [];
  };

  // Calculate range of items being shown
  const getItemRange = () => {
    const current = getDisplayCurrentPage();
    const pageSize = getDisplayPageSize();
    const total = getDisplayTotalCount();

    const start = Math.min(current * pageSize + 1, total);
    const end = Math.min((current + 1) * pageSize, total);

    return { start, end };
  };

  useEffect(() => {
    if (customPageSize) {
      setInternalPageSize(customPageSize);
    }
  }, [customPageSize]);

  const { start, end } = getItemRange();

  return (
    <Fragment>
      {/* Search and Filter Section */}
      <div className="grid grid-cols-12 lg:grid-cols-12 gap-3">
        {isSelect && (
          <div className="self-center col-span-12 lg:col-span-6">
            <label>
              Show
              <select
                name="basic_tables_length"
                aria-controls="basic_tables"
                className="px-3 py-2 form-select border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 inline-block w-auto ml-2"
                value={getDisplayPageSize()}
                onChange={(event: any) =>
                  handlePageSizeChange(Number(event.target.value))
                }
              >
                <option value="2">2</option>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </label>
          </div>
        )}

        <div className="self-center col-span-12 lg:col-span-6 lg:place-self-end">
          {isGlobalFilter && (
            <label>
              Search:{" "}
              <DebouncedInput
                value={globalFilter ?? ""}
                onChange={(value) => setGlobalFilter(String(value))}
                className="py-2 pr-4 text-sm text-topbar-item bg-topbar border border-topbar-border rounded pl-2 placeholder:text-slate-400 form-control focus-visible:outline-0 min-w-[200px] focus:border-blue-400 group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-border-dark group-data-[topbar=dark]:placeholder:text-slate-500 group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-border-brand group-data-[topbar=brand]:placeholder:text-blue-300 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:border-zink-500 group-data-[topbar=dark]:dark:text-zink-100"
                placeholder={SearchPlaceholder}
              />
            </label>
          )}
        </div>
      </div>

      {/* Table */}
      <div className={divclassName}>
        <table className={tableclassName}>
          <thead className={theadclassName}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={trclassName}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const canFilter = header.column.getCanFilter();

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${thclassName} ${
                        canSort
                          ? "cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-zink-600"
                          : ""
                      }`}
                      onClick={() => canSort && handleSort(header)}
                    >
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <div className="flex items-center justify-between">
                            <span>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            {canSort && (
                              <div className="ml-1">{getSortIcon(header)}</div>
                            )}
                          </div>
                          {canFilter && (
                            <div className="mt-1">
                              <Filter column={header.column} table={table} />
                            </div>
                          )}
                        </React.Fragment>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className={tbodyclassName}>
            {getRowModel().rows.length > 0 ? (
              getRowModel().rows.map((row) => (
                <tr key={row.id} className={trclassName}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={tdclassName}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-10 text-slate-600 dark:text-zinc-300 text-sm"
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-8 h-8 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 17v-2m3 2v-4m3 4v-6M9 3h6a2 2 0 012 2v2H7V5a2 2 0 012-2zM5 9h14v11a2 2 0 01-2 2H7a2 2 0 01-2-2V9z"
                      />
                    </svg>
                    <p className="font-medium">No records found</p>
                    <p className="text-xs text-slate-500">
                      There are currently no entries to display. Once data is
                      available, it will appear here.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>

          {isTfoot && (
            <tfoot>
              {getFooterGroups()?.map((footer: any, tfKey: number) => (
                <tr key={tfKey}>
                  {footer.headers?.map((tf: any, key: number) => (
                    <th
                      key={key}
                      className="p-3 text-left group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                    >
                      {flexRender(tf.column.columnDef.header, tf.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {isPagination && (
        <div className={PaginationClassName}>
          <div className="mb-4 grow md:mb-0">
            <div className="text-slate-500 dark:text-zink-200">
              Showing <b>{start}</b> to <b>{end}</b> of{" "}
              <b>{getDisplayTotalCount()}</b> Results
            </div>
          </div>
          <ul className="flex flex-wrap items-center gap-2 shrink-0">
            {/* Previous Page Button */}
            <li>
              <button
                type="button"
                className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 [&.active]:text-custom-500 dark:[&.active]:text-custom-500 [&.active]:bg-custom-50 dark:[&.active]:bg-custom-500/10 [&.active]:border-custom-50 dark:[&.active]:border-custom-500/10 [&.active]:hover:text-custom-700 dark:[&.active]:hover:text-custom-700 [&.disabled]:text-slate-400 dark:[&.disabled]:text-zink-300 [&.disabled]:cursor-not-allowed ${
                  !getDisplayCanPreviousPage() && "disabled"
                }`}
                onClick={() => handlePageChange(getDisplayCurrentPage() - 1)}
                disabled={!getDisplayCanPreviousPage()}
              >
                <ChevronLeft className="size-4 mr-1 rtl:rotate-180" />
                Prev
              </button>
            </li>

            {/* Page Numbers */}
            {getDisplayPageOptions().map((pageIndex: number) => (
              <li key={pageIndex}>
                <button
                  type="button"
                  className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 size-8 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 [&.active]:text-white dark:[&.active]:text-white [&.active]:bg-custom-500 dark:[&.active]:bg-custom-500 [&.active]:border-custom-500 dark:[&.active]:border-custom-500 [&.active]:hover:text-custom-700 dark:[&.active]:hover:text-custom-700 [&.disabled]:text-slate-400 dark:[&.disabled]:text-zink-300 [&.disabled]:cursor-not-allowed ${
                    getDisplayCurrentPage() === pageIndex && "active"
                  }`}
                  onClick={() => handlePageChange(pageIndex)}
                >
                  {pageIndex + 1}
                </button>
              </li>
            ))}

            {/* Next Page Button */}
            <li>
              <button
                type="button"
                className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 [&.active]:text-custom-500 dark:[&.active]:text-custom-500 [&.active]:bg-custom-50 dark:[&.active]:bg-custom-500/10 [&.active]:border-custom-50 dark:[&.active]:border-custom-500/10 [&.active]:hover:text-custom-700 dark:[&.active]:hover:text-custom-700 [&.disabled]:text-slate-400 dark:[&.disabled]:text-zink-300 [&.disabled]:cursor-not-allowed ${
                  !getDisplayCanNextPage() && "disabled"
                }`}
                onClick={() => handlePageChange(getDisplayCurrentPage() + 1)}
                disabled={!getDisplayCanNextPage()}
              >
                Next
                <ChevronRight className="size-4 ml-1 rtl:rotate-180" />
              </button>
            </li>
          </ul>
        </div>
      )}
    </Fragment>
  );
};

export default TableContainer;
